import { DEFAULT_FONT_CONFIG, FONT_SCALE_LEVELS } from "@constants";

export const FONT_CONFIG_STORAGE_KEY = "kiosk_font_config";

/**
 * FONT_NAME_REGEX contract:
 *   - Must start with an ASCII letter (A-Z, a-z).
 *   - May continue with up to 49 more letters, digits, spaces, or hyphens.
 *   - Total length: 1-50 characters.
 *   - Rejects: underscores, dots, commas, quotes, URL-unsafe characters,
 *     leading digits/whitespace, and anything CSS would need to escape.
 *   This intentionally excludes non-ASCII letters. If future projects need
 *   fonts with accented names ("Poppins Médium"), widen to /^[\p{L}][\p{L}\p{N} -]{0,49}$/u.
 */
const FONT_NAME_REGEX = /^[A-Za-z][A-Za-z0-9 -]{0,49}$/;
const HEX_COLOR_REGEX = /^#[0-9A-Fa-f]{6}$/;
const MIN_FONT_SIZE = 14;
const MAX_FONT_SIZE = 32;
export function isValidFontName(name: unknown): name is string {
    return typeof name === "string" && FONT_NAME_REGEX.test(name);
}

export function isValidFontConfig(
    value: unknown
): value is GeneralDesignFontType {
    if (!value || typeof value !== "object") return false;
    const v = value as Record<string, unknown>;
    if (!isValidFontName(v.font)) return false;
    if (typeof v.title !== "string") return false;
    if (
        typeof v.size !== "number" ||
        v.size < MIN_FONT_SIZE ||
        v.size > MAX_FONT_SIZE
    ) {
        return false;
    }
    if (v.colors !== undefined) {
        if (!v.colors || typeof v.colors !== "object") return false;
        const colors = v.colors as Record<string, unknown>;
        for (const level of FONT_SCALE_LEVELS) {
            const c = colors[level];
            if (
                c !== undefined &&
                (typeof c !== "string" || !HEX_COLOR_REGEX.test(c))
            ) {
                return false;
            }
        }
    }
    return true;
}

// Module-scope cache to avoid repeated JSON.parse + validation on hot paths.
// Invalidated by saveFontConfig (and resetCache for tests / project switch).
let cachedConfig: GeneralDesignFontType | null = null;

/**
 * Reads the font configuration from localStorage.
 * Returns DEFAULT_FONT_CONFIG if nothing is stored or the stored value is invalid.
 * Subsequent calls are served from an in-memory cache until saveFontConfig or
 * resetFontConfigCache is invoked.
 */
export function getFontConfig(): GeneralDesignFontType {
    if (cachedConfig) return cachedConfig;
    try {
        const stored = localStorage.getItem(FONT_CONFIG_STORAGE_KEY);
        if (stored) {
            const parsed = JSON.parse(stored);
            if (isValidFontConfig(parsed)) {
                cachedConfig = parsed;
                return parsed;
            }
            console.warn(
                "[fontConfig] Stored config failed validation, using default"
            );
        }
    } catch (e) {
        console.warn("[fontConfig] Error reading localStorage", e);
    }
    cachedConfig = DEFAULT_FONT_CONFIG;
    return DEFAULT_FONT_CONFIG;
}

/**
 * Persists the font configuration to localStorage.
 * Called when the user saves from the Miscellaneous (Divers) tab.
 * Rejects invalid input silently — defense-in-depth against upstream bugs.
 */
export function saveFontConfig(config: GeneralDesignFontType): void {
    if (!isValidFontConfig(config)) return;
    localStorage.setItem(FONT_CONFIG_STORAGE_KEY, JSON.stringify(config));
    cachedConfig = config;
}

/**
 * Clears the in-memory cache. Call on full project switch so the next
 * getFontConfig() re-reads localStorage.
 */
export function resetFontConfigCache(): void {
    cachedConfig = null;
}
